import { useState } from 'react';
import { UserRole } from '@klaro7/shared-types';
import { Modal, ModalPortal, ModalOverlay, ModalContent, ModalHeader, ModalTitle, ModalDescription } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useForceProfessionalOffline, useClearForcedOffline } from '../hooks/useAdminProfessionals';
import type { AdminProfessional } from '../services/professional-admin.service';

export function ForceOfflineButton({ pro }: { pro: AdminProfessional }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const force = useForceProfessionalOffline();
  const clear = useClearForcedOffline();

  if (!isSuperAdmin) return null;

  return (
    <div>
      {pro.forcedOffline && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <p className="font-semibold">
            Offline forzato
            {pro.forcedOfflineInfo
              ? ` il ${new Date(pro.forcedOfflineInfo.at).toLocaleDateString('it-IT')} da ${pro.forcedOfflineInfo.forcedByName}`
              : ''}
          </p>
          {pro.forcedOfflineInfo?.reason && <p className="mt-0.5">Motivo: {pro.forcedOfflineInfo.reason}</p>}
          <button
            type="button"
            onClick={() => clear.mutate(pro.id)}
            disabled={clear.isPending}
            className="mt-2 rounded-lg bg-brand-teal px-3 py-1 text-[11px] font-semibold text-white hover:bg-brand-teal-dark disabled:opacity-50"
          >
            Sblocca
          </button>
        </div>
      )}

      {!pro.forcedOffline && (
        <button
          type="button"
          onClick={() => { setReason(''); setOpen(true); }}
          className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
        >
          Metti offline
        </button>
      )}

      {pro.forcedOfflineHistory && pro.forcedOfflineHistory.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[10px] text-neutral-400">
          {pro.forcedOfflineHistory.slice().reverse().map((h, i) => (
            <li key={i}>
              {h.action === 'forced' ? 'Messo offline' : 'Sbloccato'} · {new Date(h.at).toLocaleString('it-IT')} · {h.byName}{h.reason ? ` · ${h.reason}` : ''}
            </li>
          ))}
        </ul>
      )}

      <Modal open={open} onOpenChange={setOpen}>
        <ModalPortal>
          <ModalOverlay />
          <ModalContent className="max-w-md">
            <ModalHeader>
              <ModalTitle>Metti offline il professionista</ModalTitle>
              <ModalDescription>Il professionista resterà loggato ma apparirà offline e non potrà ricollegarsi finché non lo sblocchi. Indica il motivo.</ModalDescription>
            </ModalHeader>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Motivo…"
              className="mt-3 w-full resize-none rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-teal/50"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-xs text-neutral-500 hover:bg-neutral-100">Annulla</button>
              <button
                type="button"
                disabled={!reason.trim() || force.isPending}
                onClick={() => force.mutate({ id: pro.id, reason: reason.trim() }, { onSuccess: () => setOpen(false) })}
                className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
              >
                Metti offline
              </button>
            </div>
          </ModalContent>
        </ModalPortal>
      </Modal>
    </div>
  );
}
